// src/config.ts
var PREFERRED_SAMPLE_RATE = 48e3;
var DEFAULT_AUDIO_BITRATE = 128e3;

// src/opus-encoder.ts
async function isOpusSupported() {
  if (!("AudioEncoder" in window)) {
    return false;
  }
  try {
    const support = await AudioEncoder.isConfigSupported({
      codec: "opus",
      sampleRate: 48e3,
      numberOfChannels: 1,
      bitrate: 128e3
    });
    return support.supported === true;
  } catch {
    return false;
  }
}
function hasAudioEncoder() {
  return "AudioEncoder" in window;
}
async function createOpusEncoder(config, onEncodedChunk) {
  if (!hasAudioEncoder()) {
    throw new Error("AudioEncoder not supported in this browser");
  }
  let isConfigured = false;
  let isClosed = false;
  let pendingFlush = null;
  let flushResolve = null;
  const encoder = new AudioEncoder({
    output: (chunk) => {
      const buffer = new Uint8Array(chunk.byteLength);
      chunk.copyTo(buffer);
      onEncodedChunk(buffer, chunk.timestamp);
    },
    error: (error) => {
      console.error("AudioEncoder error:", error.message);
    }
  });
  const codecConfig = {
    codec: "opus",
    sampleRate: config.sampleRate,
    numberOfChannels: config.channels,
    bitrate: config.bitrate,
    // Opus-specific options
    opus: {
      frameDuration: config.frameDurationMs * 1e3,
      // Convert to microseconds
      complexity: 5
      // Balance between quality and CPU (0-10)
    }
  };
  const support = await AudioEncoder.isConfigSupported(codecConfig);
  if (!support.supported) {
    throw new Error(`Opus encoder config not supported: ${JSON.stringify(config)}`);
  }
  encoder.configure(codecConfig);
  isConfigured = true;
  console.log(
    `Opus encoder configured: ${config.sampleRate}Hz, ${config.bitrate / 1e3}kbps, ${config.channels}ch, ${config.frameDurationMs}ms frames`
  );
  return {
    encode(audioData, timestamp) {
      if (isClosed || !isConfigured) {
        return;
      }
      if (audioData.length % config.channels !== 0) {
        console.error(`Audio data length ${audioData.length} not divisible by channels ${config.channels}`);
        return;
      }
      const arrayBuffer = new ArrayBuffer(audioData.byteLength);
      new Float32Array(arrayBuffer).set(audioData);
      const data = new AudioData({
        format: "f32",
        sampleRate: config.sampleRate,
        numberOfFrames: audioData.length / config.channels,
        numberOfChannels: config.channels,
        timestamp,
        data: arrayBuffer
      });
      try {
        encoder.encode(data);
      } finally {
        data.close();
      }
    },
    async flush() {
      if (isClosed || !isConfigured) {
        return;
      }
      if (pendingFlush) {
        return pendingFlush;
      }
      pendingFlush = new Promise((resolve) => {
        flushResolve = resolve;
      });
      try {
        await encoder.flush();
      } finally {
        if (flushResolve) {
          flushResolve();
          flushResolve = null;
        }
        pendingFlush = null;
      }
    },
    close() {
      if (isClosed) {
        return;
      }
      isClosed = true;
      isConfigured = false;
      try {
        encoder.close();
      } catch {
      }
      console.log("Opus encoder closed");
    },
    isReady() {
      return isConfigured && !isClosed;
    }
  };
}
function getDefaultOpusConfig(sampleRate = 48e3) {
  return {
    sampleRate,
    channels: 1,
    bitrate: 128e3,
    frameDurationMs: 20
  };
}

// src/microphone.ts
var workletUrl = new URL("./audio-worklet.js", import.meta.url);
var cachedSystemStream = null;
function isStreamActive(stream) {
  if (!stream) return false;
  const tracks = stream.getAudioTracks();
  return tracks.length > 0 && tracks.every((t) => t.readyState === "live");
}
function releaseSystemAudioStream() {
  if (cachedSystemStream) {
    cachedSystemStream.getTracks().forEach((t) => t.stop());
    cachedSystemStream = null;
    console.log("Cached system audio stream released");
  }
}
async function requestSystemAudioAccess() {
  if (isStreamActive(cachedSystemStream)) {
    console.log("Reusing existing system audio stream");
    return cachedSystemStream;
  }
  console.log("Requesting system audio access via getDisplayMedia...");
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error(
      "Screen/tab sharing is not supported in this browser. System audio capture requires Chrome or Edge."
    );
  }
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      // required by the API spec
      audio: true
    });
    stream.getVideoTracks().forEach((track) => {
      track.stop();
      stream.removeTrack(track);
    });
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      const isLinux = /Linux/.test(navigator.userAgent) && !/Android/.test(navigator.userAgent);
      const hint = isLinux ? "On Linux, only browser tab sharing includes audio. Share a browser tab (not a window or entire screen) and check 'Share tab audio'." : "Make sure you select a tab or screen with audio enabled (in Chrome, check the 'Share audio' checkbox).";
      throw new Error(`No audio track in the shared source. ${hint}`);
    }
    cachedSystemStream = stream;
    audioTracks[0].addEventListener("ended", () => {
      console.log("System audio track ended, clearing cache");
      cachedSystemStream = null;
    });
    console.log("System audio access granted, audio tracks:", audioTracks.length);
    return stream;
  } catch (error) {
    if (error instanceof DOMException) {
      if (error.name === "NotAllowedError") {
        throw new Error("Screen sharing was cancelled or denied.", { cause: error });
      }
    }
    throw error;
  }
}
async function requestCombinedAudioAccess() {
  console.log("Requesting combined mic + system audio...");
  const systemStream = await requestSystemAudioAccess();
  let micStream;
  try {
    micStream = await requestMicrophoneAccess();
  } catch (error) {
    systemStream.getTracks().forEach((t) => t.stop());
    throw error;
  }
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const destination = audioContext.createMediaStreamDestination();
  const micSource = audioContext.createMediaStreamSource(micStream);
  const systemSource = audioContext.createMediaStreamSource(systemStream);
  micSource.connect(destination);
  systemSource.connect(destination);
  const mergedStream = destination.stream;
  console.log("Combined audio stream created");
  const cleanup = () => {
    micStream.getTracks().forEach((t) => t.stop());
    if (systemStream !== cachedSystemStream) {
      systemStream.getTracks().forEach((t) => t.stop());
    }
    audioContext.close().catch(() => {
    });
  };
  const stopMerged = () => {
    cleanup();
    mergedStream.getTracks().forEach((t) => t.stop());
  };
  micStream.getAudioTracks()[0]?.addEventListener("ended", stopMerged);
  systemStream.getAudioTracks()[0]?.addEventListener("ended", stopMerged);
  mergedStream.getAudioTracks()[0]?.addEventListener("ended", cleanup);
  return mergedStream;
}
async function requestAudioAccess(source = "microphone") {
  switch (source) {
    case "system":
      return requestSystemAudioAccess();
    case "both":
      return requestCombinedAudioAccess();
    case "microphone":
    default:
      return requestMicrophoneAccess();
  }
}
async function requestMicrophoneAccess() {
  console.log("Requesting microphone access...");
  const isFirefoxAndroid = /Android.*Firefox/.test(navigator.userAgent);
  if (isFirefoxAndroid) {
    console.warn("Firefox Android detected - getUserMedia may not work reliably");
  }
  try {
    console.log("Attempt 1: Requesting with constraints (stereo preferred, echo/noise suppression)");
    const stream = await Promise.race([
      navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: { ideal: 2 },
          echoCancellation: true,
          noiseSuppression: true
        }
      }),
      new Promise((_, reject) => setTimeout(() => reject(new Error("getUserMedia timeout with constraints")), 15e3))
    ]);
    console.log("Microphone access granted with constraints");
    return stream;
  } catch (error) {
    console.warn("Failed with constraints, trying simpler request...", error instanceof Error ? error.message : error);
    try {
      console.log("Attempt 2: Requesting with minimal constraints");
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({ audio: true }),
        new Promise((_, reject) => setTimeout(() => reject(new Error("getUserMedia timeout with minimal constraints")), 15e3))
      ]);
      console.log("Microphone access granted with minimal constraints");
      return stream;
    } catch (fallbackError) {
      console.error("Microphone access failed:", fallbackError instanceof Error ? fallbackError.message : fallbackError);
      console.error("Browser user agent:", navigator.userAgent);
      console.error("mediaDevices available:", !!navigator.mediaDevices);
      console.error("getUserMedia available:", !!navigator.mediaDevices?.getUserMedia);
      if (error instanceof DOMException) {
        if (error.name === "NotAllowedError") {
          throw new Error("Microphone permission denied (or revoked)", { cause: error });
        }
        if (error.name === "NotFoundError") {
          throw new Error("No microphone device found. Check that your device has a microphone and it's not in use by another app.", {
            cause: error
          });
        }
        if (error.name === "NotSupportedError") {
          throw new Error("getUserMedia not supported in this browser", { cause: error });
        }
      }
      const message = fallbackError instanceof Error ? fallbackError.message : "unknown error";
      throw new Error(
        `Microphone access failed: ${message}. This may indicate the device has no microphone or Firefox Android requires additional permissions.`,
        { cause: fallbackError }
      );
    }
  }
}
function detectChannelCount(stream) {
  const tracks = stream.getAudioTracks();
  if (tracks.length > 0) {
    const settings = tracks[0].getSettings();
    return settings.channelCount || 1;
  }
  return 1;
}
async function detectEncodingMode() {
  const opusSupported = await isOpusSupported();
  if (opusSupported) {
    console.log("Opus encoding supported via WebCodecs");
    return "opus";
  }
  console.log("Opus not supported, falling back to PCM");
  return "pcm";
}
async function getEncodingConfig(preferredBitrate) {
  const mode = await detectEncodingMode();
  const bitrate = preferredBitrate ?? DEFAULT_AUDIO_BITRATE;
  if (mode === "opus") {
    return {
      mode: "opus",
      bitrate,
      sampleRate: PREFERRED_SAMPLE_RATE
      // Native rate, will use AudioContext's rate
    };
  }
  return {
    mode: "pcm",
    bitrate: 0,
    // Not applicable for PCM
    sampleRate: 16e3
  };
}
async function createAudioChunkStream(stream, config) {
  const encodingConfig = config ?? await getEncodingConfig();
  const channelCount = detectChannelCount(stream);
  console.log(`Detected ${channelCount} channel(s) from microphone`);
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const actualSampleRate = audioContext.sampleRate;
  console.log(`AudioContext created with sample rate: ${actualSampleRate}Hz`);
  const workletConfig = encodingConfig.mode === "opus" ? {
    frameDurationMs: 20,
    outputFormat: "float32",
    targetSampleRate: actualSampleRate,
    // No resampling for Opus
    channels: channelCount
  } : {
    frameDurationMs: 40,
    outputFormat: "pcm16",
    targetSampleRate: 16e3,
    // Resample to 16kHz for PCM
    channels: channelCount
  };
  try {
    await audioContext.audioWorklet.addModule(workletUrl);
  } catch (error) {
    const message = error instanceof Error ? error.message : "unknown error";
    throw new Error(`Failed to load audio worklet: ${message}`, { cause: error });
  }
  const worklet = new AudioWorkletNode(audioContext, "audio-processor", {
    numberOfInputs: 1,
    numberOfOutputs: 0,
    processorOptions: workletConfig
  });
  const source = audioContext.createMediaStreamSource(stream);
  source.connect(worklet);
  let opusEncoder = null;
  if (encodingConfig.mode === "opus") {
    const bitrate = channelCount === 2 ? 192e3 : encodingConfig.bitrate;
    const opusConfig = {
      sampleRate: actualSampleRate,
      channels: channelCount,
      bitrate,
      frameDurationMs: 20
    };
    try {
      opusEncoder = await createOpusEncoder(opusConfig, () => {
      });
    } catch (error) {
      if (channelCount === 2) {
        console.warn("Stereo encoding failed, falling back to mono:", error);
        const monoConfig = {
          sampleRate: actualSampleRate,
          channels: 1,
          bitrate: encodingConfig.bitrate,
          frameDurationMs: 20
        };
        try {
          opusEncoder = await createOpusEncoder(monoConfig, () => {
          });
        } catch (monoError) {
          console.warn("Mono encoding also failed, continuing with raw audio:", monoError);
        }
      } else {
        console.warn("Failed to create Opus encoder, falling back to PCM:", error);
      }
    }
  }
  return createChunkIterable(audioContext, worklet, stream, opusEncoder, encodingConfig, channelCount);
}
function createChunkIterable(audioContext, worklet, stream, opusEncoder, config, channelCount) {
  return {
    async *[Symbol.asyncIterator]() {
      console.log(`Audio stream: Starting (mode=${config.mode})...`);
      const chunkQueue = [];
      let resolveNext = null;
      let streamEnded = false;
      let opusOutputHandler = null;
      if (opusEncoder) {
        const actualSampleRate = audioContext.sampleRate;
        const bitrate = channelCount === 2 ? 192e3 : config.bitrate;
        const opusConfig = {
          sampleRate: actualSampleRate,
          channels: channelCount,
          bitrate,
          frameDurationMs: 20
        };
        opusEncoder.close();
        opusOutputHandler = (chunk) => {
          if (resolveNext) {
            resolveNext(chunk);
            resolveNext = null;
          } else {
            chunkQueue.push(chunk);
          }
        };
        try {
          opusEncoder = await createOpusEncoder(opusConfig, (chunk) => {
            opusOutputHandler?.(chunk);
          });
        } catch (error) {
          console.error("Failed to create Opus encoder:", error);
          opusEncoder = null;
        }
      }
      worklet.port.onmessage = (event) => {
        if (event.data.type === "audio") {
          const buffer = event.data.data;
          const timestamp = event.data.timestamp;
          if (opusEncoder && opusEncoder.isReady()) {
            const float32Data = new Float32Array(buffer);
            opusEncoder.encode(float32Data, timestamp);
          } else {
            const chunk = new Uint8Array(buffer);
            if (resolveNext) {
              resolveNext(chunk);
              resolveNext = null;
            } else {
              chunkQueue.push(chunk);
            }
          }
        }
      };
      const getNextChunk = () => {
        if (chunkQueue.length > 0) {
          return Promise.resolve(chunkQueue.shift());
        }
        if (streamEnded) {
          return Promise.resolve(null);
        }
        return new Promise((resolve) => {
          resolveNext = resolve;
        });
      };
      try {
        let chunkCount = 0;
        while (stream.active && !streamEnded) {
          const chunk = await getNextChunk();
          if (!chunk) {
            console.log("Audio stream: No more chunks");
            break;
          }
          chunkCount++;
          if (chunkCount === 1) {
            console.log(`Audio stream: First chunk received, size: ${chunk.byteLength} bytes, mode: ${config.mode}`);
          }
          yield chunk;
        }
        console.log(`Audio stream: Ended, chunks received: ${chunkCount}`);
      } finally {
        streamEnded = true;
        worklet.port.onmessage = null;
        if (opusEncoder) {
          try {
            await opusEncoder.flush();
          } catch {
          }
          opusEncoder.close();
        }
        if (stream !== cachedSystemStream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        worklet.disconnect();
        await audioContext.close();
        console.log("Audio stream: Cleanup complete");
      }
    }
  };
}
async function createPcmAudioStream(stream) {
  return createAudioChunkStream(stream, {
    mode: "pcm",
    bitrate: 0,
    sampleRate: 16e3
  });
}

// src/media-recorder.ts
function getSupportedMimeType() {
  const mimeTypes = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus"
  ];
  for (const mimeType of mimeTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }
  return null;
}
function isWebmOpusSupported() {
  const mimeType = getSupportedMimeType();
  return mimeType !== null && mimeType.includes("webm");
}
function createMediaRecorderStream(mediaStream, config, onDataAvailable) {
  const mimeType = getSupportedMimeType();
  if (!mimeType) {
    throw new Error("No supported audio mimeType found for MediaRecorder");
  }
  const mediaRecorder = new MediaRecorder(mediaStream, {
    mimeType,
    audioBitsPerSecond: config.bitrate
  });
  console.log(`MediaRecorder created with mimeType: ${mediaRecorder.mimeType}, bitrate: ${config.bitrate}`);
  let isActive = false;
  let chunkCount = 0;
  mediaRecorder.ondataavailable = (event) => {
    if (event.data.size > 0) {
      chunkCount++;
      if (chunkCount === 1) {
        console.log(`MediaRecorder: First chunk (contains EBML header), size: ${event.data.size} bytes`);
      } else if (chunkCount <= 5) {
        console.log(`MediaRecorder: Chunk #${chunkCount}, size: ${event.data.size} bytes`);
      }
      onDataAvailable(event.data);
    }
  };
  mediaRecorder.onstart = () => {
    console.log("MediaRecorder started");
    isActive = true;
  };
  mediaRecorder.onstop = () => {
    console.log(`MediaRecorder stopped, total chunks: ${chunkCount}`);
    isActive = false;
  };
  mediaRecorder.onerror = (event) => {
    console.error("MediaRecorder error:", event);
    isActive = false;
  };
  return {
    start() {
      if (!isActive && mediaRecorder.state === "inactive") {
        chunkCount = 0;
        mediaRecorder.start(config.timesliceMs);
      }
    },
    stop() {
      if (mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
    },
    isRecording() {
      return isActive && mediaRecorder.state === "recording";
    },
    getMimeType() {
      return mediaRecorder.mimeType;
    }
  };
}
function getDefaultMediaRecorderConfig() {
  return {
    bitrate: 128e3,
    timesliceMs: 100
    // 100ms chunks for low latency
  };
}

// src/ogg-muxer.ts
var FLAG_BOS = 2;
var FLAG_EOS = 4;
var MAX_SEGMENT_SIZE = 255;
var crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let crc = i << 24;
    for (let j = 0; j < 8; j++) {
      crc = crc << 1 ^ (crc & 2147483648 ? 79764919 : 0);
    }
    table[i] = crc >>> 0;
  }
  return table;
})();
function oggCrc32(data) {
  let crc = 0;
  for (let i = 0; i < data.length; i++) {
    crc = crc << 8 ^ crcTable[(crc >>> 24 ^ data[i]) & 255];
  }
  return crc >>> 0;
}
function writeUint16LE(buffer, offset, value) {
  buffer[offset] = value & 255;
  buffer[offset + 1] = value >> 8 & 255;
}
function writeUint32LE(buffer, offset, value) {
  buffer[offset] = value & 255;
  buffer[offset + 1] = value >> 8 & 255;
  buffer[offset + 2] = value >> 16 & 255;
  buffer[offset + 3] = value >> 24 & 255;
}
function writeUint64LE(buffer, offset, value) {
  writeUint32LE(buffer, offset, value >>> 0);
  writeUint32LE(buffer, offset + 4, Math.floor(value / 4294967296) >>> 0);
}
function createOggPage(serialNumber, pageSequence, granulePosition, flags, packets) {
  const segments = [];
  for (const packet of packets) {
    let remaining = packet.length;
    while (remaining >= MAX_SEGMENT_SIZE) {
      segments.push(MAX_SEGMENT_SIZE);
      remaining -= MAX_SEGMENT_SIZE;
    }
    segments.push(remaining);
  }
  const headerSize = 27 + segments.length;
  const dataSize = packets.reduce((sum, p) => sum + p.length, 0);
  const pageSize = headerSize + dataSize;
  const page = new Uint8Array(pageSize);
  page[0] = 79;
  page[1] = 103;
  page[2] = 103;
  page[3] = 83;
  page[4] = 0;
  page[5] = flags;
  writeUint64LE(page, 6, granulePosition);
  writeUint32LE(page, 14, serialNumber);
  writeUint32LE(page, 18, pageSequence);
  page[26] = segments.length;
  for (let i = 0; i < segments.length; i++) {
    page[27 + i] = segments[i];
  }
  let offset = headerSize;
  for (const packet of packets) {
    page.set(packet, offset);
    offset += packet.length;
  }
  const crc = oggCrc32(page);
  writeUint32LE(page, 22, crc);
  return page;
}
function createOpusHead(config) {
  const head = new Uint8Array(19);
  head[0] = 79;
  head[1] = 112;
  head[2] = 117;
  head[3] = 115;
  head[4] = 72;
  head[5] = 101;
  head[6] = 97;
  head[7] = 100;
  head[8] = 1;
  head[9] = config.channels;
  const preskip = config.preskip ?? 312;
  writeUint16LE(head, 10, preskip);
  writeUint32LE(head, 12, config.sampleRate);
  writeUint16LE(head, 16, 0);
  head[18] = 0;
  return head;
}
function createOpusTags() {
  const vendor = "live-tranz";
  const vendorBytes = new TextEncoder().encode(vendor);
  const tags = new Uint8Array(8 + 4 + vendorBytes.length + 4);
  tags[0] = 79;
  tags[1] = 112;
  tags[2] = 117;
  tags[3] = 115;
  tags[4] = 84;
  tags[5] = 97;
  tags[6] = 103;
  tags[7] = 115;
  writeUint32LE(tags, 8, vendorBytes.length);
  tags.set(vendorBytes, 12);
  writeUint32LE(tags, 12 + vendorBytes.length, 0);
  return tags;
}
function createOggMuxer(config) {
  const serialNumber = Math.floor(Math.random() * 4294967295);
  let pageSequence = 0;
  let granulePosition = 0;
  const samplesPerMs = config.sampleRate / 1e3;
  const getHeaderPages = () => {
    const headPacket = createOpusHead(config);
    const headPage = createOggPage(
      serialNumber,
      pageSequence++,
      0,
      // granule = 0 for header
      FLAG_BOS,
      [headPacket]
    );
    const tagsPacket = createOpusTags();
    const tagsPage = createOggPage(
      serialNumber,
      pageSequence++,
      0,
      // granule = 0 for header
      0,
      // no flags
      [tagsPacket]
    );
    const combined = new Uint8Array(headPage.length + tagsPage.length);
    combined.set(headPage, 0);
    combined.set(tagsPage, headPage.length);
    return combined;
  };
  const muxPacket = (opusPacket, durationMs) => {
    const samples = Math.round(durationMs * samplesPerMs);
    granulePosition += samples;
    return createOggPage(
      serialNumber,
      pageSequence++,
      granulePosition,
      0,
      // no flags
      [opusPacket]
    );
  };
  const finalize = () => {
    return createOggPage(
      serialNumber,
      pageSequence++,
      granulePosition,
      FLAG_EOS,
      []
    );
  };
  const getGranulePosition = () => granulePosition;
  return {
    getHeaderPages,
    muxPacket,
    finalize,
    getGranulePosition
  };
}
function concatBuffers(buffers) {
  const totalLength = buffers.reduce((sum, b) => sum + b.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const buffer of buffers) {
    result.set(buffer, offset);
    offset += buffer.length;
  }
  return result;
}

// src/opfs-storage.ts
async function isOPFSSupported() {
  if (!("storage" in navigator) || !("getDirectory" in navigator.storage)) {
    return false;
  }
  if (typeof Worker === "undefined") {
    return false;
  }
  try {
    await navigator.storage.getDirectory();
    return true;
  } catch {
    return false;
  }
}
function createOPFSStorage() {
  let worker = new Worker(new URL("./opfs-storage.worker.js", import.meta.url), { type: "module" });
  let finalized = false;
  const handleWorkerError = (error) => {
    console.error("OPFS worker error:", error.message);
  };
  worker.addEventListener("error", handleWorkerError);
  const initialize = (config, sessionId) => {
    if (!worker) {
      return Promise.reject(new Error("Worker terminated"));
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("OPFS initialization timeout"));
      }, 5e3);
      const handler = (event) => {
        worker.removeEventListener("message", handler);
        clearTimeout(timeout);
        if (event.data.type === "ready") {
          resolve();
        } else if (event.data.type === "error") {
          reject(new Error(event.data.error));
        }
      };
      worker.addEventListener("message", handler);
      worker.postMessage({ type: "init", config, sessionId });
    });
  };
  const storeChunk = (timestamp, opusPacket) => {
    if (!worker) {
      console.warn("OPFS worker terminated, chunk not stored");
      return;
    }
    worker.postMessage({
      type: "chunk",
      timestamp,
      opusPacket
    });
  };
  const finalize = () => {
    if (!worker) {
      return Promise.reject(new Error("Worker terminated"));
    }
    if (finalized) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("OPFS finalization timeout"));
      }, 5e3);
      const handler = (event) => {
        worker.removeEventListener("message", handler);
        clearTimeout(timeout);
        if (event.data.type === "finalized") {
          finalized = true;
          resolve();
        } else if (event.data.type === "error") {
          reject(new Error(event.data.error));
        }
      };
      worker.addEventListener("message", handler);
      worker.postMessage({ type: "finalize" });
    });
  };
  const exportFile = () => {
    if (!worker) {
      return Promise.reject(new Error("Worker terminated"));
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("OPFS export timeout"));
      }, 1e4);
      const handler = (event) => {
        worker.removeEventListener("message", handler);
        clearTimeout(timeout);
        if (event.data.type === "exported") {
          resolve(event.data.blob);
        } else if (event.data.type === "error") {
          reject(new Error(event.data.error));
        }
      };
      worker.addEventListener("message", handler);
      worker.postMessage({ type: "export" });
    });
  };
  const clearSession = () => {
    if (!worker) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve();
      }, 5e3);
      const handler = (_event) => {
        worker.removeEventListener("message", handler);
        clearTimeout(timeout);
        resolve();
      };
      worker.addEventListener("message", handler);
      worker.postMessage({ type: "clear" });
    });
  };
  const terminate = () => {
    if (worker) {
      worker.removeEventListener("error", handleWorkerError);
      worker.terminate();
      worker = null;
    }
  };
  return {
    initialize,
    storeChunk,
    finalize,
    exportFile,
    clearSession,
    terminate
  };
}

// src/audio-storage.ts
function isFileSystemAccessSupported() {
  return "showDirectoryPicker" in window;
}
function generateSessionId() {
  const now = /* @__PURE__ */ new Date();
  const date = now.toISOString().split("T")[0];
  const time = now.toTimeString().split(" ")[0].replace(/:/g, "-");
  const random = Math.random().toString(36).substring(2, 6);
  return `session-${date}-${time}-${random}`;
}
function formatTimestamp(date = /* @__PURE__ */ new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}
function createAudioStorage() {
  let sessionId = generateSessionId();
  let directoryHandle = null;
  let fileHandle = null;
  let writableStream = null;
  let oggMuxer = null;
  let audioConfig = null;
  let chunkIndex = 0;
  let opfsStorage = null;
  let inMemoryChunks = [];
  let showedMemoryWarning = false;
  const getMode = () => {
    if (directoryHandle) return "filesystem";
    if (opfsStorage) return "opfs";
    return "memory";
  };
  const initialize = async (config) => {
    audioConfig = config;
    if (!directoryHandle) {
      if (await isOPFSSupported()) {
        try {
          opfsStorage = createOPFSStorage();
          await opfsStorage.initialize(config, sessionId);
          console.log("OPFS storage initialized");
        } catch (error) {
          console.warn("OPFS init failed, using in-memory storage:", error);
          opfsStorage = null;
        }
      } else {
        console.warn("OPFS not supported, using in-memory storage");
      }
    }
  };
  const selectFolder = async () => {
    if (!isFileSystemAccessSupported()) {
      console.log("File System Access API not supported");
      return false;
    }
    try {
      directoryHandle = await window.showDirectoryPicker({
        mode: "readwrite",
        startIn: "music"
      });
      const permission = await directoryHandle.requestPermission({ mode: "readwrite" });
      if (permission !== "granted") {
        console.warn("Write permission not granted");
        directoryHandle = null;
        return false;
      }
      console.log("Folder selected:", directoryHandle.name);
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Folder selection cancelled");
      } else {
        console.error("Failed to select folder:", error);
      }
      return false;
    }
  };
  const hasSelectedFolder = () => {
    return directoryHandle !== null;
  };
  const storeChunk = async (timestamp, opusPacket) => {
    if (directoryHandle) {
      try {
        if (!fileHandle) {
          if (!audioConfig) {
            throw new Error("AudioStorage not initialized - call initialize() first");
          }
          const filename = `recording-${sessionId}.opus`;
          fileHandle = await directoryHandle.getFileHandle(filename, { create: true });
          writableStream = await fileHandle.createWritable();
          oggMuxer = createOggMuxer({
            sampleRate: audioConfig.sampleRate,
            channels: audioConfig.channels
          });
          const headers = oggMuxer.getHeaderPages();
          const headerBuffer = headers.buffer.slice(headers.byteOffset, headers.byteOffset + headers.byteLength);
          await writableStream.write(headerBuffer);
          console.log(`Created Ogg audio file: ${filename}`);
        }
        if (writableStream && oggMuxer && audioConfig) {
          const oggPage = oggMuxer.muxPacket(opusPacket, audioConfig.frameDurationMs);
          const pageBuffer = oggPage.buffer.slice(oggPage.byteOffset, oggPage.byteOffset + oggPage.byteLength);
          await writableStream.write(pageBuffer);
          chunkIndex++;
        }
      } catch (error) {
        console.error("Failed to write to file, falling back to in-memory:", error);
        directoryHandle = null;
        fileHandle = null;
        writableStream = null;
        oggMuxer = null;
        inMemoryChunks.push({ timestamp, data: opusPacket });
        chunkIndex++;
        if (chunkIndex === 1 && !showedMemoryWarning) {
          showedMemoryWarning = true;
          console.warn("\u26A0\uFE0F  Audio storage: Using in-memory storage - recording will be lost if you refresh the page!");
        }
      }
    } else if (opfsStorage) {
      opfsStorage.storeChunk(timestamp, opusPacket);
      chunkIndex++;
      if (chunkIndex === 1) {
        console.log("Audio storage: Storing to OPFS, sessionId:", sessionId);
      }
    } else {
      inMemoryChunks.push({ timestamp, data: opusPacket });
      chunkIndex++;
      if (chunkIndex === 1 && !showedMemoryWarning) {
        showedMemoryWarning = true;
        console.warn("\u26A0\uFE0F  Audio storage: Using in-memory storage - recording will be lost if you refresh the page!");
        console.log("Audio storage: Storing to memory, sessionId:", sessionId);
      }
    }
  };
  const clearSession = async () => {
    if (opfsStorage) {
      await opfsStorage.clearSession();
    }
    if (directoryHandle && fileHandle) {
      try {
        if (writableStream) {
          await writableStream.close();
          writableStream = null;
        }
        const filename = `recording-${sessionId}.opus`;
        await directoryHandle.removeEntry(filename);
        fileHandle = null;
        oggMuxer = null;
        console.log(`Deleted audio file: ${filename}`);
      } catch (error) {
        console.warn("Failed to delete file:", error);
      }
    }
    inMemoryChunks = [];
    showedMemoryWarning = false;
    chunkIndex = 0;
  };
  const exportToFile = async (filename) => {
    if (opfsStorage) {
      const blob = await opfsStorage.exportFile();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename ?? `recording-${formatTimestamp()}.opus`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      console.log(`Exported from OPFS to ${a.download}`);
    } else if (directoryHandle) {
      console.log("FSAA file already saved to folder");
    } else {
      if (inMemoryChunks.length === 0) {
        throw new Error("No audio data to export");
      }
      if (!audioConfig) {
        throw new Error("AudioStorage not initialized");
      }
      const exportMuxer = createOggMuxer({
        sampleRate: audioConfig.sampleRate,
        channels: audioConfig.channels
      });
      const pages = [];
      pages.push(exportMuxer.getHeaderPages());
      for (const chunk of inMemoryChunks) {
        const page = exportMuxer.muxPacket(chunk.data, audioConfig.frameDurationMs);
        pages.push(page);
      }
      pages.push(exportMuxer.finalize());
      const oggData = concatBuffers(pages);
      const blobBuffer = oggData.buffer.slice(oggData.byteOffset, oggData.byteOffset + oggData.byteLength);
      const blob = new Blob([blobBuffer], { type: "audio/ogg" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename ?? `recording-${formatTimestamp()}.opus`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      console.log(`Exported ${oggData.byteLength} bytes from memory to ${a.download}`);
    }
  };
  const getSessionId = () => sessionId;
  const startNewSession = () => {
    const oldSessionId = sessionId;
    if (opfsStorage) {
      opfsStorage.terminate();
      opfsStorage = null;
    }
    if (writableStream) {
      writableStream.close().catch(() => {
      });
      writableStream = null;
    }
    fileHandle = null;
    oggMuxer = null;
    inMemoryChunks = [];
    showedMemoryWarning = false;
    chunkIndex = 0;
    sessionId = generateSessionId();
    console.log("Audio storage: Started new session. Old sessionId:", oldSessionId, "New sessionId:", sessionId);
    return sessionId;
  };
  const getStoredSize = async () => {
    if (directoryHandle && fileHandle) {
      try {
        const file = await fileHandle.getFile();
        return file.size;
      } catch {
        return 0;
      }
    }
    if (inMemoryChunks.length > 0) {
      return inMemoryChunks.reduce((total, chunk) => total + chunk.data.byteLength, 0);
    }
    return 0;
  };
  const finalize = async () => {
    if (opfsStorage) {
      await opfsStorage.finalize();
    } else if (writableStream && oggMuxer) {
      try {
        const eosPage = oggMuxer.finalize();
        const eosBuffer = eosPage.buffer.slice(eosPage.byteOffset, eosPage.byteOffset + eosPage.byteLength);
        await writableStream.write(eosBuffer);
        await writableStream.close();
        console.log("Ogg file finalized with EOS marker");
      } catch (error) {
        console.error("Failed to finalize Ogg file:", error);
      }
      writableStream = null;
    } else if (inMemoryChunks.length > 0) {
      console.log(`In-memory storage: ${inMemoryChunks.length} chunks ready for export`);
    }
  };
  return {
    getMode,
    initialize,
    selectFolder,
    hasSelectedFolder,
    storeChunk,
    clearSession,
    exportToFile,
    getSessionId,
    startNewSession,
    getStoredSize,
    finalize
  };
}

// src/sentenceDetector.ts
function createSentenceDetector(config) {
  const { silenceTimeoutMs = 1500, onSentenceComplete } = config;
  let buffer = "";
  let silenceTimer = null;
  let isDestroyed = false;
  const clearSilenceTimer = () => {
    if (silenceTimer !== null) {
      clearTimeout(silenceTimer);
      silenceTimer = null;
    }
  };
  const resetSilenceTimer = () => {
    clearSilenceTimer();
    silenceTimer = setTimeout(() => {
      if (!isDestroyed && buffer.trim().length > 0) {
        const sentence = buffer.trim();
        buffer = "";
        onSentenceComplete(sentence);
      }
    }, silenceTimeoutMs);
  };
  const extractSentence = (text) => {
    const match = text.match(/^(.*?)([.!?])(\s*)$/);
    if (match) {
      const beforePunct = match[1];
      const punc = match[2];
      const after = match[3];
      if (beforePunct.length > 0 && (after.length > 0 || text.endsWith(punc))) {
        return [beforePunct + punc, true];
      }
    }
    return [text, false];
  };
  return {
    pushText(delta) {
      if (isDestroyed) return;
      buffer += delta;
      const [extracted, hasSentence] = extractSentence(buffer);
      if (hasSentence) {
        clearSilenceTimer();
        const sentence = extracted.trim();
        if (sentence.length > 0) {
          onSentenceComplete(sentence);
        }
        buffer = "";
      } else {
        resetSilenceTimer();
      }
    },
    flush() {
      if (isDestroyed) return;
      clearSilenceTimer();
      const sentence = buffer.trim();
      buffer = "";
      if (sentence.length > 0) {
        onSentenceComplete(sentence);
      }
    },
    destroy() {
      if (isDestroyed) return;
      isDestroyed = true;
      clearSilenceTimer();
      buffer = "";
    }
  };
}

// src/wake-lock.ts
function isWakeLockSupported() {
  return "wakeLock" in navigator;
}
function createWakeLock() {
  let wakeLock = null;
  let visibilityHandler = null;
  const acquire = async () => {
    if (!isWakeLockSupported()) {
      console.log("Wake Lock API not supported");
      return false;
    }
    try {
      wakeLock = await navigator.wakeLock.request("screen");
      console.log("Wake lock acquired");
      wakeLock.addEventListener("release", () => {
        console.log("Wake lock released by system");
        wakeLock = null;
      });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      console.warn("Failed to acquire wake lock:", message);
      return false;
    }
  };
  const release = async () => {
    if (wakeLock) {
      try {
        await wakeLock.release();
        console.log("Wake lock released");
      } catch (error) {
        console.warn("Error releasing wake lock:", error);
      }
      wakeLock = null;
    }
  };
  const isActive = () => {
    return wakeLock !== null && !wakeLock.released;
  };
  const destroy = () => {
    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
  };
  return {
    acquire,
    release,
    isActive,
    destroy
  };
}
function createPersistentWakeLock(shouldReacquire) {
  const service = createWakeLock();
  let visibilityHandler = null;
  visibilityHandler = async () => {
    if (document.visibilityState === "visible" && shouldReacquire() && !service.isActive()) {
      console.log("Page visible again, reacquiring wake lock...");
      await service.acquire();
    }
  };
  document.addEventListener("visibilitychange", visibilityHandler);
  return {
    ...service,
    destroy: () => {
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
        visibilityHandler = null;
      }
      service.destroy();
    }
  };
}
export {
  DEFAULT_AUDIO_BITRATE,
  PREFERRED_SAMPLE_RATE,
  concatBuffers,
  createAudioChunkStream,
  createAudioStorage,
  createMediaRecorderStream,
  createOPFSStorage,
  createOggMuxer,
  createOpusEncoder,
  createPcmAudioStream,
  createPersistentWakeLock,
  createSentenceDetector,
  createWakeLock,
  detectChannelCount,
  detectEncodingMode,
  getDefaultMediaRecorderConfig,
  getDefaultOpusConfig,
  getEncodingConfig,
  getSupportedMimeType,
  hasAudioEncoder,
  isFileSystemAccessSupported,
  isOPFSSupported,
  isOpusSupported,
  isWakeLockSupported,
  isWebmOpusSupported,
  releaseSystemAudioStream,
  requestAudioAccess,
  requestCombinedAudioAccess,
  requestMicrophoneAccess,
  requestSystemAudioAccess
};
